import { collection, getDocs } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../config/firebaseConfig';
import { transitionToWaiting } from '../../utils/astrapos/stageTransitions';
import GameCountdown from './GameCountdown';

/**
 * LEADERBOARD stage: Show room-based leaderboard before resetting
 * Players can view their performance and wait for next cycle
 */
const LeaderboardStage=({ roomId, roomData, leaderboardDuration=60 }) => {
  const [leaderboardData, setLeaderboardData]=useState([]);
  const [filterBy, setFilterBy]=useState('blocks');
  const [transitionInProgress, setTransitionInProgress]=useState(false);

  const currentCycle=roomData.gameState?.currentCycle||1;

  // Build leaderboard data from transactions and blocks
  useEffect(() => {
    const fetchLeaderboardData=async () => {
      try {
        // Get all transactions (cumulative across all cycles)
        const txSnapshot=await getDocs(
          collection(db, 'rooms', roomId, 'transactions')
        );

        // Get all blocks (cumulative across all cycles)
        const blockSnapshot=await getDocs(
          collection(db, 'rooms', roomId, 'blocks')
        );

        // Build stats
        const stats={};

        // Count blocks per miner
        blockSnapshot.forEach((doc) => {
          const block=doc.data();
          if (block.transactions&&block.transactions.length>0) {
            const minersInBlock=new Map();

            // Identify unique miners in this block
            block.transactions.forEach((tx) => {
              const minerPk=tx.minerPublicKey;
              if (minerPk) {
                minersInBlock.set(minerPk, tx.minerEmail||'Unknown');
              }
            });

            // Increment block count for each unique miner
            minersInBlock.forEach((email, minerPk) => {
              if (!stats[minerPk]) {
                stats[minerPk]={
                  email: email,
                  blocks: 0,
                  txCreated: 0,
                  txVerified: 0,
                };
              }
              stats[minerPk].blocks++;
            });
          }
        });

        // Count transactions (excluding system reward transactions)
        txSnapshot.forEach((doc) => {
          const tx=doc.data();
          const senderPk=tx.sender;
          const minerPk=tx.minerPublicKey;

          // Skip system reward transactions
          if (
            tx.senderEmail==='system'||
            senderPk==='AstraMiner/AstraPOS/AstraHack'
          ) {
            return;
          }

          if (senderPk) {
            if (!stats[senderPk]) {
              stats[senderPk]={
                email: tx.senderEmail||'Unknown',
                blocks: 0,
                txCreated: 0,
                txVerified: 0,
              };
            }
            stats[senderPk].txCreated++;
          }

          if (minerPk&&tx.verified) {
            if (!stats[minerPk]) {
              stats[minerPk]={
                email: tx.minerEmail||'Unknown',
                blocks: 0,
                txCreated: 0,
                txVerified: 0,
              };
            }
            stats[minerPk].txVerified++;
          }
        });

        // Convert to array
        const leaderboard=Object.entries(stats).map(([pk, data]) => ({
          publicKey: pk,
          ...data,
        }));

        setLeaderboardData(leaderboard);
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
      }
    };

    fetchLeaderboardData();
  }, [roomId, currentCycle]);

  // Handle countdown expiry
  const handleLeaderboardExpiry=async () => {
    if (transitionInProgress) return;
    setTransitionInProgress(true);

    try {
      await transitionToWaiting(roomId);
      // UI will update automatically via real-time listener
    } catch (error) {
      console.error('Error transitioning to WAITING:', error);
      setTransitionInProgress(false);
    }
  };

  // Sort leaderboard based on filter
  const sortedLeaderboard=[...leaderboardData].sort((a, b) => {
    switch (filterBy) {
      case 'blocks':
        return b.blocks-a.blocks;
      case 'created':
        return b.txCreated-a.txCreated;
      case 'verified':
        return b.txVerified-a.txVerified;
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-white mb-4">Round Complete!</h1>
        <p className="text-white/60 mb-6">
          Leaderboard for Cycle {currentCycle}
        </p>

        {/* Countdown */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 inline-block">
          <p className="text-white/80 text-sm mb-2">Next round starts in</p>
          <GameCountdown
            endsAt={roomData.gameState?.stageEndsAt}
            onExpiry={handleLeaderboardExpiry}
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 justify-center flex-wrap">
        {[
          { value: 'blocks', label: '⛏️ Blocks Mined' },
          { value: 'created', label: '📝 Tx Created' },
          { value: 'verified', label: '✓ Tx Verified' },
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilterBy(tab.value)}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${filterBy===tab.value
                ? 'bg-purple-500 text-white'
                :'bg-white/10 text-white/60 hover:bg-white/20'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Leaderboard */}
      <div className="bg-[#2A2F4A] backdrop-blur rounded-xl p-8 border border-[#3A3F5A]">
        <h2 className="text-white font-semibold text-lg mb-6">Leaderboard</h2>

        {sortedLeaderboard.length>0? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-white/60">#</th>
                  <th className="text-left py-3 px-4 text-white/60">Player</th>
                  <th className="text-center py-3 px-4 text-white/60">
                    ⛏️ Blocks
                  </th>
                  <th className="text-center py-3 px-4 text-white/60">
                    📝 Created
                  </th>
                  <th className="text-center py-3 px-4 text-white/60">
                    ✓ Verified
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedLeaderboard.map((entry, idx) => (
                  <tr
                    key={entry.publicKey}
                    className="border-b border-white/5 hover:bg-white/5 transition-all"
                  >
                    <td className="py-3 px-4">
                      <span className="text-white font-bold text-lg">
                        {idx+1}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-white font-semibold">
                          {entry.email}
                        </p>
                        <p className="text-white/40 text-xs">
                          {entry.publicKey.substring(0, 10)}...
                        </p>
                      </div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-white font-semibold">
                        {entry.blocks}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-white font-semibold">
                        {entry.txCreated}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-white font-semibold">
                        {entry.txVerified}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ):(
          <p className="text-white/60 text-center py-8">No data yet</p>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-blue-500/10 backdrop-blur rounded-xl p-6 border border-blue-500/20">
        <p className="text-blue-200 text-sm">
          <strong>Round Complete!</strong> The game will reset shortly and
          return to the waiting phase for the next round. Players will get
          another chance to stake and compete for the Validator role.
        </p>
      </div>
    </div>
  );
};

export default LeaderboardStage;
